import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';

type Theme = 'dark' | 'light' | 'sunrise';

interface ThemeContextType {
    theme: Theme;
    toggleTheme: () => void;
    setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
    const [theme, setThemeState] = useState<Theme>(() => {
        const saved = localStorage.getItem('theme');
        return (saved === 'light' || saved === 'dark' || saved === 'sunrise') ? saved : 'dark';
    });

    useEffect(() => {
        localStorage.setItem('theme', theme);
        const root = document.documentElement;
        root.classList.remove('dark', 'light', 'sunrise');
        root.classList.add(theme);
    }, [theme]);

    const toggleTheme = () => setThemeState(prev => {
        if (prev === 'dark') return 'light';
        if (prev === 'light') return 'sunrise';
        return 'dark';
    });
    const setTheme = (t: Theme) => setThemeState(t);

    return (
        <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
            {children}
        </ThemeContext.Provider>
    );
}

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};
